import React from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { LP, viewAllSx } from './launchpadStyles';

// ──────────────────────────────────────────────────────────────────────────────
// LaunchpadAtoms — shared presentational atoms reused across Launchpad panels.
//   • ActionButton — the green "Do / Reply / Respond" pill (was duplicated 3×).
//   • EmptyState   — centred muted message for empty panels.
// ──────────────────────────────────────────────────────────────────────────────

// tone='default' = the soft mint pill; tone='whatsapp' = WhatsApp-green solid
// (used by the "Waiting on a reply" widget so Reply reads like WhatsApp's send).
const ACTION_TONES = {
    default: {
        color: '#0b7a55',
        border: '1px solid #c2ead8',
        bgcolor: '#eafaf3',
        '&:hover': { bgcolor: '#d4f5e9', borderColor: '#a6e0c8' },
        '&:active': { bgcolor: '#c4efdc' },
    },
    whatsapp: {
        color: '#fff',
        border: '1px solid #1da851',
        bgcolor: '#25d366',
        '&:hover': { bgcolor: '#1eb858', borderColor: '#1a9c4d' },
        '&:active': { bgcolor: '#1a9c4d' },
    },
};

export const ActionButton = ({
    onClick,
    children,
    tone = 'default',
    startIcon = null,
    disabled = false,
}) => (
    <Box
        component='button'
        onClick={onClick}
        disabled={disabled}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 700,
            borderRadius: '8px',
            padding: { xs: '5px 8px', sm: '5px 12px' },
            cursor: disabled ? 'not-allowed' : 'pointer',
            flexShrink: 0,
            lineHeight: 1.2,
            maxWidth: { xs: 96, sm: 'none' },
            minWidth: 0,
            whiteSpace: 'normal',
            textAlign: 'center',
            transition: 'background-color 0.14s ease, border-color 0.14s ease',
            ...(ACTION_TONES[tone] || ACTION_TONES.default),
            ...(disabled
                ? {
                      opacity: 0.55,
                      '&:hover': ACTION_TONES[tone]?.bgcolor
                          ? { bgcolor: ACTION_TONES[tone].bgcolor }
                          : undefined,
                  }
                : {}),
        }}
    >
        {startIcon && <Iconify icon={startIcon} width={14} height={14} sx={{ flexShrink: 0 }} />}
        {children}
    </Box>
);

ActionButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    tone: PropTypes.oneOf(['default', 'whatsapp']),
    startIcon: PropTypes.string,
    disabled: PropTypes.bool,
};

export const ActionSurface = ({
    children,
    onClick = undefined,
    disabled = false,
    sx = {},
    ariaLabel = undefined,
}) => (
    <ButtonBase
        component='button'
        type='button'
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={onClick}
        sx={{
            display: 'block',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            textAlign: 'left',
            borderRadius: '12px',
            cursor: disabled ? 'default' : 'pointer',
            '&.Mui-disabled': { opacity: 1 },
            '&:focus-visible': {
                outline: `2px solid ${LP.blue}`,
                outlineOffset: 2,
            },
            ...sx,
        }}
    >
        {children}
    </ButtonBase>
);

ActionSurface.propTypes = {
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    sx: PropTypes.object,
    ariaLabel: PropTypes.string,
};

export const TextAction = ({ children, onClick, ariaLabel = undefined, sx = {} }) => (
    <ButtonBase
        component='button'
        type='button'
        onClick={onClick}
        aria-label={ariaLabel}
        sx={{
            ...viewAllSx,
            border: 0,
            bgcolor: 'transparent',
            p: 0,
            minWidth: 0,
            borderRadius: '4px',
            '&:focus-visible': {
                outline: `2px solid ${LP.blue}`,
                outlineOffset: 2,
            },
            ...sx,
        }}
    >
        {children}
    </ButtonBase>
);

TextAction.propTypes = {
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
    sx: PropTypes.object,
};

// Deterministic soft-tinted avatar palette — index picked from a name hash so the
// same patient keeps the same colour across renders.
const AVATAR_TINTS = [
    { bg: '#e7f0ff', fg: '#2f6bff' },
    { bg: '#e7f8f1', fg: '#0b8a5f' },
    { bg: '#fdeef0', fg: '#d23b50' },
    { bg: '#fdf3e3', fg: '#b9740a' },
    { bg: '#efeaff', fg: '#6a4bd0' },
    { bg: '#e6f6f7', fg: '#0e7c8a' },
];

function tintFor(seed) {
    const s = String(seed || '');
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 2147483647;
    return AVATAR_TINTS[h % AVATAR_TINTS.length];
}

export const InitialAvatar = ({ name, ring }) => {
    const clean = String(name || '').trim();
    const initials =
        clean
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0])
            .join('')
            .toUpperCase() || '·';
    const tint = tintFor(clean || 'x');
    return (
        <Box
            sx={{
                width: 30,
                height: 30,
                flexShrink: 0,
                borderRadius: '50%',
                bgcolor: tint.bg,
                color: tint.fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.2px',
                boxShadow: ring ? '0 0 0 2px #fff, 0 0 0 3.5px #13b67f' : 'none',
            }}
        >
            {initials}
        </Box>
    );
};

InitialAvatar.propTypes = {
    name: PropTypes.string,
    ring: PropTypes.bool,
};

InitialAvatar.defaultProps = { name: '', ring: false };

export const EmptyState = ({ icon, children }) => (
    <Box
        sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            py: '22px',
            minHeight: '88px',
        }}
    >
        {icon && (
            <Typography sx={{ fontSize: '18px', lineHeight: 1, opacity: 0.5 }}>{icon}</Typography>
        )}
        <Typography sx={{ fontSize: '12px', color: LP.sub, opacity: 0.85 }}>{children}</Typography>
    </Box>
);

EmptyState.propTypes = {
    icon: PropTypes.node,
    children: PropTypes.node.isRequired,
};

EmptyState.defaultProps = { icon: null };

export const ErrorState = ({
    children = 'Could not load this section',
    onRetry = null,
    actionLabel = 'Retry',
}) => (
    <Box
        sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            py: '22px',
            minHeight: '88px',
        }}
    >
        <Typography
            sx={{
                fontSize: '12px',
                color: LP.red,
                bgcolor: LP.redSoft,
                borderRadius: '9px',
                px: '10px',
                py: '7px',
                textAlign: 'center',
            }}
        >
            {children}
        </Typography>
        {onRetry && (
            <Box
                component='button'
                type='button'
                onClick={onRetry}
                sx={{
                    border: `1px solid ${LP.line}`,
                    bgcolor: '#fff',
                    color: LP.blue,
                    borderRadius: '8px',
                    px: '10px',
                    py: '5px',
                    fontSize: '11px',
                    fontWeight: 800,
                    lineHeight: 1.2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: LP.hover },
                }}
            >
                {actionLabel}
            </Box>
        )}
    </Box>
);

ErrorState.propTypes = {
    children: PropTypes.node,
    onRetry: PropTypes.func,
    actionLabel: PropTypes.string,
};
